import { ForgotCredentialsLink, RememberMe } from './styles'
import AuthTemplate from 'pages/Authentication/components/AuthTemplate'
import Illus from 'pages/Authentication/components/AuthTemplate/assets/login.png'
import AuthTitle from 'pages/Authentication/components/AuthTitle'
import { Formik } from 'formik'
import * as Yup from 'yup'
import Form from 'components/Form'
import Input from 'components/Input'
import Checkbox from 'components/Checkbox'
import { Trans, useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import SubmitButton from 'components/SubmitButton'
import { useMutation } from '@apollo/client'
import GraphQLHandler from 'components/GraphQLHandler'
import { LOGIN, LoginResult, LoginVariables } from './mutations'
import { displayErrors, DoMutation, hasNoError } from 'globals/graphql'
import ValidateOnEnter from 'components/ValidateOnEnter'

interface LoginForm {
  email?: string
  password?: string
  rememberMe?: boolean
}

const initialValues: LoginForm = {
  rememberMe: false
}

const validation = (t: TFunction): Yup.SchemaOf<LoginForm> =>
  Yup.object({
    email: Yup.string().required(t('global.error.required-f', { field: t('global.email') })),
    password: Yup.string().required(t('global.error.required', { field: t('global.password') })),
    rememberMe: Yup.boolean()
  })

const Login = () => {
  const { t } = useTranslation()

  return (
    <AuthTemplate rightPartImg={Illus}>
      <AuthTitle
        title={
          <Trans i18nKey='login.title'>
            Bienvenue dans votre
            <br />
            <b>espace partenaire</b>
          </Trans>
        }
      />

      <GraphQLHandler
        mutations={{
          login: useMutation<LoginResult, LoginVariables>(LOGIN, displayErrors)
        }}
      >
        {({
          mutations: {
            login: [doLogin, loginData]
          }
        }) => (
          <Formik
            initialValues={initialValues}
            validationSchema={validation(t)}
            onSubmit={onSubmitLoginForm(doLogin)}
            isInitialValid={validation(t).isValidSync(initialValues)}
          >
            {({ handleSubmit }) => (
              <ValidateOnEnter handleSubmit={handleSubmit} name='login'>
                <Form>
                  <Input name='email' type='email' label={t('global.email')} defaultFocus />

                  <Input name='password' type='password' label={t('global.password')} bottomPadding={13} />

                  <RememberMe>
                    <Checkbox name='rememberMe' label={t('login.rememberMe')} />
                  </RememberMe>

                  <SubmitButton text={t('login.submit')} loading={loginData.loading} />

                  <ForgotCredentialsLink to='/forgot-credentials'>
                    {t('login.forgot-credentials')}
                  </ForgotCredentialsLink>
                </Form>
              </ValidateOnEnter>
            )}
          </Formik>
        )}
      </GraphQLHandler>
    </AuthTemplate>
  )
}

const onSubmitLoginForm = (doLogin: DoMutation<LoginResult, LoginVariables>) => (values: LoginForm) => {
  if (values.email && values.password) {
    doLogin({
      variables: {
        email: values.email,
        password: values.password,
        rememberMe: values.rememberMe
      }
    }).then(
      hasNoError((result: LoginResult) => {
        localStorage.setItem('token', result.userSignIn.token)
        window.location.href = '/'
      })
    )
  }
}

export default Login
